import { motion } from 'framer-motion'
import * as React from 'react'
import { CardImageText } from '#app/components/ui/card-image-text.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Tag } from '#app/components/ui/tag.tsx'
import { cn } from '#app/utils/misc.tsx'
import { type InsuranceType } from '../insurance-section'

export interface SegurosCardsSectionProps {
	/**
	 * Section title
	 */
	title: string
	/**
	 * Section subtitle
	 */
	subtitle?: string
	/**
	 * Tag/chip text
	 */
	tagText: string
	/**
	 * Tag icon name
	 */
	tagIcon?: string
	/**
	 * Tag color
	 */
	tagColor?: 'blue' | 'green'
	/**
	 * List of insurance cards to display
	 */
	insurances: InsuranceType[]
	/**
	 * Card click handler
	 */
	onInsuranceClick?: (insuranceId: string | undefined) => void
	/**
	 * Additional CSS classes
	 */
	className?: string
}

const SegurosCardsSection = React.forwardRef<
	HTMLElement,
	SegurosCardsSectionProps
>(
	(
		{
			title,
			subtitle,
			tagText,
			tagIcon = 'user-full-view',
			tagColor = 'blue',
			insurances,
			onInsuranceClick,
			className,
			...props
		},
		ref,
	) => {
		// Split title into first word and rest for color styling
		const titleParts = title.split(' ')
		const firstPart = titleParts.slice(0, 2).join(' ') // "Seguros para"
		const secondPart = titleParts.slice(2).join(' ') // rest of title

		return (
			<section
				ref={ref}
				className={cn('bg-white py-12 md:py-20', className)}
				{...props}
			>
				<div className="mx-auto px-4 md:px-6">
					<div className="flex w-full flex-col items-center justify-start gap-8">
						{/* Tag/Chip */}
						<Tag
							size="l"
							corner="rounded"
							variant="secondary"
							color={tagColor}
							appearance="solid"
							leftIcon={tagIcon as any}
							option="text-icon-left"
						>
							{tagText}
						</Tag>

						{/* Title and Subtitle */}
						<div className="flex w-full flex-col items-center justify-start gap-4">
							<h2 className="font-parkinsans w-full text-center text-[28px] leading-[38px] font-medium text-blue-800 not-italic md:text-[40px] md:leading-[56px]">
								<span>{firstPart} </span>
								<span
									className={cn(
										tagColor === 'blue' ? 'text-green-500' : 'text-green-500',
									)}
								>
									{secondPart}
								</span>
							</h2>

							{subtitle && (
								<p className="font-inter w-full text-center text-[16px] leading-[24px] font-normal text-neutral-600 not-italic md:text-[18px] md:leading-[36px]">
									{subtitle}
								</p>
							)}
						</div>

						{/* Insurance Cards Grid - Mobile 2x4, Desktop responsive */}
						<div className="mx-auto mt-8 grid w-full max-w-[1200px] grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
							{insurances.map((insurance, index) => (
								<motion.div
									key={insurance.id}
									initial={{ opacity: 0, scale: 0.9, y: 20 }}
									whileInView={{ opacity: 1, scale: 1, y: 0 }}
									viewport={{ once: true }}
									transition={{
										duration: 0.3,
										delay: index * 0.05,
										ease: [0.25, 0.46, 0.45, 0.94],
									}}
									className="w-full"
								>
									<CardImageText
										title={insurance.title}
										color={insurance.color}
										height={206}
										icon={
											<Icon
												name={insurance.iconName as any}
												className={cn(
													'h-8 w-8 md:h-10 md:w-10',
													insurance.color === 'blue'
														? 'text-blue-800'
														: 'text-green-800',
												)}
											/>
										}
										onClick={() => onInsuranceClick?.(insurance.link)}
										className="w-full cursor-pointer"
									/>
								</motion.div>
							))}
						</div>
					</div>
				</div>
			</section>
		)
	},
)

SegurosCardsSection.displayName = 'SegurosCardsSection'

export { SegurosCardsSection }
